// Menu — an accessible dropdown of commands/choices on the React Aria
// foundation (ADR-029), per COMPONENTS §Overlay patterns. Use it for a uniform
// list you arrow through and pick from (every entity's ⋯ door — `ActionMenu` —
// the project switcher, the sysadmin's gear). For a card of MIXED content (header +
// controls + a mini-form), use <Popover> instead. For site navigation (top bar,
// sidebar), use <NavLink> in a <nav> — that is NOT a menu.
//
// React Aria owns open/close, arrow-roving, type-ahead, Escape + focus return,
// collision-aware positioning, and RTL. We own 100% of the markup + tokens. The
// trigger is any focusable element (our IconButton / button), wrapped in React
// Aria's <Pressable>. Items with `href` navigate through React Router (the
// RouterProvider wired in RootLayout) — real links, modifier-click works.
//
// TWO FACES, one trigger (ADR-080 §12): on a desk the anchored panel; on a phone
// (the `md` tier) the family's `Sheet` from the bottom, the menu's name as its
// heading, dismissed by tapping outside — the sheet reads the trigger's state
// (no `open` given), so every menu moves at once, no consumer edited. Rows are
// finger-sized under a coarse pointer (`pointer-coarse:`), whatever the face.
// No height cap: the foundation sizes the panel to the space it has. A searched
// menu pins its box above the rows on both faces (ADR-080 §11): the rows scroll,
// the box stays.

import type { ComponentProps, ReactElement, ReactNode } from 'react';
import {
  Autocomplete,
  MenuTrigger,
  Popover as AriaPopover,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  Separator,
  Pressable,
  Text,
} from 'react-aria-components';
import type { PopoverProps as AriaPopoverProps } from 'react-aria-components';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { PickerSearchBox } from './PickerList.js';
import { PHONE_QUERY, Sheet } from './Sheet.js';

export type MenuPlacement = NonNullable<AriaPopoverProps['placement']>;

type PressableChild = ComponentProps<typeof Pressable>['children'];

export interface MenuProps {
  /** The focusable trigger (IconButton / button) — forwards ref + spreads props. */
  renderTrigger: () => ReactElement;
  /** Accessible name for the menu (required — WCAG 4.1.2). */
  label: string;
  /** Panel placement relative to the trigger; logical edges flip under RTL. Default 'bottom start'. */
  placement?: MenuPlacement;
  /** Menu contents — MenuItem / MenuSeparator. */
  children: ReactNode;
  panelClassName?: string;
  /** Forces the face (stories; the gate runs at one viewport). Default: the `md` tier. */
  face?: 'desk' | 'phone';
  /** The family's search box above the rows (a list past 8 — ADR-080 §11): the host owns the text and shows the rows
   *  that match; the foundation's Autocomplete walks the rows from the box (ArrowDown · Enter). The text is cleared
   *  when the menu closes. */
  search?: { value: string; onChange: (text: string) => void; placeholder?: string };
}

export function Menu({
  renderTrigger,
  label,
  placement = 'bottom start',
  children,
  panelClassName = '',
  face,
  search,
}: MenuProps) {
  const { t } = useTranslation('common');
  const narrow = useMediaQuery(PHONE_QUERY);
  const phone = face ? face === 'phone' : narrow;
  // The rows scroll on their own (the box above them stays); a searched menu with no match says so.
  const menu = (
    <AriaMenu
      aria-label={label}
      className={`min-h-0 overflow-y-auto py-1 outline-none ${panelClassName}`}
      renderEmptyState={
        search
          ? () => (
              <p className="px-3 py-2 text-sm text-[var(--color-text-secondary)]">
                {t('picker.noResults', { q: search.value.trim() })}
              </p>
            )
          : undefined
      }
    >
      {children}
    </AriaMenu>
  );
  const box = search ? (
    <PickerSearchBox value={search.value} onChange={search.onChange} placeholder={search.placeholder} focusAtOpen touch={phone} />
  ) : null;
  // The sheet pins the box in its own slot (above the scrolling body); the desk panel stacks it above the rows.
  // The Autocomplete wraps BOTH the box and the rows (the sheet renders `pinned` inside its dialog, so the context reaches it).
  const panel = phone ? (
    <Sheet title={label} face="bottom" pinned={box}>
      {menu}
    </Sheet>
  ) : (
    <AriaPopover
      placement={placement}
      offset={4}
      className={
        'z-dropdown flex min-w-[13rem] max-w-[18rem] flex-col overflow-hidden rounded-md border border-[var(--color-border)] ' +
        'bg-[var(--color-bg-overlay)] shadow-overlay ' +
        'data-[entering]:o-pop-in data-[exiting]:o-pop-out'
      }
    >
      {box && <div className="shrink-0 px-1.5 pt-1.5">{box}</div>}
      {menu}
    </AriaPopover>
  );
  return (
    <MenuTrigger
      onOpenChange={(open) => {
        if (!open) search?.onChange('');
      }}
    >
      <Pressable>{renderTrigger() as PressableChild}</Pressable>
      {search ? <Autocomplete>{panel}</Autocomplete> : panel}
    </MenuTrigger>
  );
}

// ── Items ───────────────────────────────────────────────────────────────────

// `data-[focused]` is React Aria's active-item state (keyboard roving AND
// pointer hover resolve to it), so one rule covers the highlight the hand-rolled
// menu split across hover + focus-visible.
const itemBase =
  'flex items-center gap-2.5 px-3 py-2 text-sm text-start cursor-pointer outline-none pointer-coarse:min-h-12 pointer-coarse:text-base ' +
  'data-[focused]:bg-[var(--color-bg-hover)] data-[pressed]:bg-[var(--color-border)] data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed';

export interface MenuItemProps {
  /** Navigate on select (real link via the React Aria RouterProvider). */
  href?: string;
  /** Run on select (for non-navigation actions). */
  onAction?: () => void;
  icon?: ReactNode;
  children: ReactNode;
  /** Trailing element (check, badge, status). */
  trailing?: ReactNode;
  /** A second line under the label — a disabled row says why ("After the ticket is completed"). It describes the
   *  row (`aria-describedby`); the label alone names it, so a long note never squeezes the label away. */
  description?: string;
  isDisabled?: boolean;
  /** `danger` tints the label + icon for an exit/destructive action. */
  tone?: 'default' | 'danger';
  /** The words type-ahead matches on (a menu without `search`; a searched menu's host matches its own rows) — needed
   *  when `children` is not a plain string (a two-line face). */
  textValue?: string;
}

export function MenuItem({
  href,
  onAction,
  icon,
  children,
  trailing,
  description,
  isDisabled,
  tone = 'default',
  textValue,
}: MenuItemProps) {
  const toneClass =
    tone === 'danger' ? 'text-[var(--color-danger)]' : 'text-[var(--color-text-primary)]';
  return (
    <AriaMenuItem
      // Only pass href for link items — passing `href={undefined}` to a
      // handler/onAction item makes React Aria treat it as an (empty) link
      // ("empty string passed to href"). Spread it conditionally so an action
      // item is a clean button-role menuitem.
      {...(href ? { href } : {})}
      onAction={onAction}
      isDisabled={isDisabled}
      // RAC sets the accessible name from text content; the trailing/ icon are
      // decorative. textValue keeps type-ahead matching the visible label.
      textValue={textValue ?? (typeof children === 'string' ? children : undefined)}
      className={`${itemBase} ${toneClass}`}
    >
      {icon}
      {description ? (
        <span className="flex min-w-0 flex-1 flex-col">
          <Text slot="label" className="truncate">
            {children}
          </Text>
          <Text slot="description" className="text-xs text-[var(--color-text-secondary)]">
            {description}
          </Text>
        </span>
      ) : (
        <span className="flex-1 truncate">{children}</span>
      )}
      {trailing}
    </AriaMenuItem>
  );
}

export function MenuSeparator() {
  return <Separator className="my-1 border-t border-[var(--color-divider)]" />;
}
